import { businessWeekKeys, shiftDateKey, type AvailableSession, type SessionMutationResult } from "@teamna/shared";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  Button,
  CalendarIcon,
  Callout,
  ChipRow,
  EmptyState,
  ErrorState,
  FactRow,
  Field,
  InlineError,
  LockIcon,
  OwnerListRow,
  OwnerSessionCard,
  PageHeader,
  PlusCircleIcon,
  SegmentedControl,
  Sheet,
  SkeletonList,
  SuccessNotice,
  WeekStrip,
  type ChipOption,
  type WeekDay,
  useSessionEnded,
} from "../components/index.ts";
import { DESKTOP_QUERY, useMediaQuery } from "../hooks/use-media-query.ts";
import { useLabels } from "@teamna/web-client/hooks/use-labels.ts";
import { useOpenMemberThread } from "@teamna/web-client/hooks/use-messaging.ts";
import {
  useActiveBusiness,
  useCancelSession,
  useCreateSession,
  useMoveSession,
  useOwnerGroups,
  useOwnerSessionAttendees,
  useOwnerSessions,
} from "@teamna/web-client/hooks/use-owner.ts";
import {
  dayKey,
  formatDateTime,
  formatDayHeading,
  formatDayOfMonth,
  formatTime,
  formatWeekRange,
  formatWeekdayShort,
  instantFromZonedTime,
} from "@teamna/web-client/lib/format.ts";
import { SessionsView } from "./schedule-grid.tsx";
import { TimetableView } from "./timetable/TimetableScreen.tsx";
import { attendanceBadge } from "./today/progress.ts";
import { TodayView } from "./today/TodayView.tsx";
import { useSessionProgress } from "./today/use-today.ts";
import { SessionCover } from "../covers/cover-requests.tsx";
import { SessionPeople, SessionStaffChoice, sessionFacts } from "./session-controls.tsx";
import { OwnerGate, useOwnerNotice, type OwnerNotice } from "./shell.tsx";

/**
 * The owner's week.
 *
 * Building a schedule on a phone is the known weak point of a mobile-first owner experience, so
 * this screen optimises for the four things that actually happen every week: look at the week, add
 * a one-off session, move one, and cancel one. Weekly templates are a setup task and are not here.
 *
 * A week rather than the member's eight, because an owner is answering "what is on, and is it
 * full", and eight weeks of that is a wall.
 *
 * The week is a strip, not a scroll. The first version stacked all seven days into one agenda and
 * it read as today's list: the nearest day was always at the top, nothing above the fold said
 * which day that was, and the other six were below a scroll that nobody made. The strip shows the
 * whole week at once with a count under each day, so the shape of the week lands before anything
 * is tapped, and the list underneath is one day with its own date on it.
 */

/** The hours a UAE business actually runs. A full 24-hour picker on a phone is a scroll, not a choice. */
const START_HOURS = [7, 9, 11, 15, 17, 18, 19, 20];

/** The shifts a move is ever made by. Anything else is a new session, not a nudge. */
const SHIFT_MINUTES = [-60, -30, -15, 15, 30, 60];

/**
 * The Calendar page (named Schedule until 2026-10-19). At 1024px and up it has three views on one
 * calendar: Day, the sessions of one day by coach with attendance one click away (`today/TodayView.tsx`,
 * the default; it opens on today, arrows move one day; named Today until 2026-10-21); Week, the sessions of one week, where a change is for one date (`schedule-grid.tsx`,
 * named Sessions before); and Schedule, the weekly times from now on (`timetable/`, named Timetable
 * before). `?view=week` opens Week, `?view=timetable` opens Schedule, and `/owner/timetable` sends
 * there. Only the names of Week and Schedule changed. Below 1024px the day list, unchanged.
 */
export function OwnerScheduleScreen() {
  const desktop = useMediaQuery(DESKTOP_QUERY);
  const search = useSearch({ strict: false }) as { view?: string };
  return desktop ? <OwnerSchedulePage /> : <OwnerScheduleList timetableNote={search.view === "timetable"} />;
}

type ScheduleView = "day" | "week" | "timetable";

function OwnerSchedulePage() {
  const { business, isLoading, error, may } = useActiveBusiness();
  const { label } = useLabels();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { view?: string };
  // The Schedule view is the timetable editor: not for the front desk (T1), which sees Day and Week.
  const editsTimetable = may("readTimetableEditor");
  const view: ScheduleView = search.view === "timetable" && editsTimetable ? "timetable" : search.view === "week" ? "week" : "day";
  const families = label("member") === label("participant") ? label("member", { plural: true }) : "families";
  const Families = `${families.charAt(0).toUpperCase()}${families.slice(1)}`;

  function show(next: ScheduleView) {
    void navigate({ to: ".", search: next === "day" ? {} : { view: next } });
  }

  let body;
  if (business) {
    if (view === "timetable") body = <TimetableView />;
    else if (view === "week") {
      body = <SessionsView key={business.businessId} businessId={business.businessId} timezone={business.timezone || "Asia/Dubai"} onTimetable={() => show("timetable")} />;
    } else {
      body = (
        <TodayView key={business.businessId} business={business} />
      );
    }
  } else if (isLoading) body = <SkeletonList />;
  else if (error) body = <ErrorState error={error} />;
  else {
    body = (
      <EmptyState
        icon={<LockIcon size={30} />}
        title="No business to manage"
        message="This account does not run a business. Ask us to set one up, or use the member side to see your own schedule."
      />
    );
  }

  return (
    // The calendar uses the full width of the window, in all three views (`AppShell`).
    <div data-page-width="full">
      <PageHeader
        title="Calendar"
        description={
          view === "day"
            ? "The sessions of one day. Use the arrows to see another day. Click a session to open it. Take attendance is at the bottom of the panel."
            : view === "week"
              ? "The sessions of each week. A change here is for one date."
              : `The weekly times from now on. ${Families} see a change after you publish it.`
        }
        actions={
          <SegmentedControl
            label="View"
            value={view}
            options={[
              { value: "day", label: "Day" },
              { value: "week", label: "Week" },
              ...(editsTimetable ? [{ value: "timetable" as const, label: "Schedule" }] : []),
            ]}
            onChange={show}
          />
        }
      />
      {body}
    </div>
  );
}

/** `timetableNote`: a link to the Timetable view on a phone, which has no timetable editor. */
function OwnerScheduleList({ timetableNote = false }: { timetableNote?: boolean }) {
  const { business, managed, isLoading, error, select, may } = useActiveBusiness();
  const [weekOffset, setWeekOffset] = useState(0);
  // The tapped session, with the business it came from: a switch to another business closes it.
  const [selected, setSelected] = useState<{ businessId: string; session: AvailableSession } | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const notice = useOwnerNotice();

  const businessId = business?.businessId ?? null;
  const timezone = business?.timezone;

  /**
   * The seven days on screen: the business week (`businessWeekKeys`, Saturday first), the same
   * seven days the desktop grid and the mobile app show as "This week". Built from calendar keys in
   * the business's timezone, so the window is stable while the screen is open and the query key
   * underneath it does not change on every render.
   */
  const todayKey = dayKey(new Date().toISOString(), timezone);
  const range = useMemo(() => {
    const keys = businessWeekKeys(todayKey, weekOffset);
    return {
      keys,
      from: instantFromZonedTime(keys[0]!, 0, 0, timezone),
      to: instantFromZonedTime(shiftDateKey(keys[6]!, 1), 0, 0, timezone),
    };
  }, [todayKey, weekOffset, timezone]);

  const sessions = useOwnerSessions({ businessId, from: range.from, to: range.to });
  // The open session as the week reads it now, so the panel shows a change at once (a coach picked,
  // a client added: #ai-feedback 2026-10-26), the same way the desktop panel does. The row from the
  // tap stays only while the week is read again, and only for the business it came from.
  const openSession =
    selected && selected.businessId === businessId
      ? ((sessions.data ?? []).find((row) => row.id === selected.session.id) ?? selected.session)
      : null;
  // The registers of the same week, for where attendance is on each card.
  const progressOf = useSessionProgress(businessId, range.from, range.to);

  /** One entry per day of the window, whether or not anything is on it. */
  const days = useMemo(() => {
    const counts = new Map<string, number>();
    for (const session of sessions.data ?? []) {
      const key = dayKey(session.startsAt, timezone);
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return range.keys.map((key) => {
      const iso = instantFromZonedTime(key, 12, 0, timezone);
      return {
        key,
        iso,
        weekday: formatWeekdayShort(iso, timezone),
        dayOfMonth: formatDayOfMonth(iso, timezone),
        count: counts.get(key) ?? 0,
        isToday: key === todayKey,
      };
    });
  }, [sessions.data, timezone, range.keys, todayKey]);

  /*
   * Which day is open.
   *
   * Held as a day key rather than an index so that stepping to another week keeps the weekday: an
   * owner checking "what does Thursday look like next week" should land on Thursday. `resolveDay`
   * below falls back to today when the current week is open and to the first day otherwise, which
   * is the only sensible answer for a week that does not contain today at all.
   */
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const activeDay = resolveDay(days, selectedKey);

  const daySessions = useMemo(() => {
    if (!activeDay) return [];
    return (sessions.data ?? [])
      .filter((session) => dayKey(session.startsAt, timezone) === activeDay.key)
      .sort((a, b) => a.startsAt.localeCompare(b.startsAt));
  }, [sessions.data, timezone, activeDay]);

  const chips = useMemo<ChipOption[]>(
    () => managed.map((row) => ({ id: row.businessId, label: row.name })),
    [managed],
  );

  const stripDays: WeekDay[] = days.map(({ key, weekday, dayOfMonth, count, isToday }) => ({
    key,
    weekday,
    dayOfMonth,
    count,
    isToday,
  }));

  function stepWeek(by: number) {
    setWeekOffset((week) => week + by);
    // The key names a date in the week being left, so it cannot match anything in the new one.
    // Clearing it lets `resolveDay` pick the same weekday in the week that just arrived.
    setSelectedKey((current) => (current ? shiftDateKey(current, by * 7) : null));
  }

  return (
    <OwnerGate
      title="Calendar"
      business={business}
      isLoading={isLoading}
      error={error}
      managedCount={managed.length}
    >
      {chips.length > 1 ? (
        <div className="pb-4">
          <ChipRow
            options={chips}
            selectedId={business?.businessId ?? ""}
            onSelect={select}
            label="Which business to manage"
          />
        </div>
      ) : null}

      {timetableNote && may("readTimetableEditor") ? (
        <div className="pb-4">
          <Callout tone="quiet" title="Change the schedule on a computer">
            The schedule editor needs a wider screen. Open Teamna on a computer to change the weekly times. The sessions of the week are here.
          </Callout>
        </div>
      ) : null}

      {notice.value ? (
        <SuccessNotice
          title={notice.value.title}
          message={notice.value.message}
          onDismiss={notice.clear}
        />
      ) : null}

      <WeekStrip
        label={weekLabel(weekOffset)}
        range={formatWeekRange(
          instantFromZonedTime(range.keys[0]!, 12, 0, timezone),
          instantFromZonedTime(range.keys[6]!, 12, 0, timezone),
          timezone,
        )}
        days={stripDays}
        selectedKey={activeDay?.key ?? ""}
        onSelect={setSelectedKey}
        onPrevious={() => stepWeek(-1)}
        onNext={() => stepWeek(1)}
      />

      {/*
        A ghost rather than a bordered button. Two full-width cards of chrome above the first
        session card push the content a third of the way down a 390px phone, and adding a session
        is the rarer of the two things this screen is opened for.
      */}
      {may("addSession") ? (
        <Button
          variant="ghost"
          size="lg"
          fullWidth
          className="mt-1.5"
          onClick={() => setShowCreate(true)}
        >
          <PlusCircleIcon size={19} />
          Add a session
        </Button>
      ) : null}

      <div className="pt-3">
        {/*
          The day's own heading, spelled out. This is the line that stops the list below reading as
          "the next few sessions" rather than "Thursday".
        */}
        <div className="flex items-baseline justify-between gap-3 pb-2.5">
          <h2 className="text-[17px] font-semibold">
            {activeDay ? formatDayHeading(activeDay.iso, timezone) : ""}
          </h2>
          <span className="shrink-0 text-[11px] font-semibold tracking-[0.08em] text-ink-3 uppercase">
            {sessions.data ? plural(daySessions.length, "session", "sessions") : "Loading"}
          </span>
        </div>

        {sessions.isLoading && !sessions.data ? (
          <SkeletonList />
        ) : sessions.error && !sessions.data ? (
          <ErrorState error={sessions.error} onRetry={() => void sessions.refetch()} />
        ) : daySessions.length === 0 ? (
          <EmptyState
            icon={<CalendarIcon size={30} />}
            title="Nothing on this day"
            message={
              (sessions.data?.length ?? 0) > 0
                ? "This day is clear. The other days this week are on the strip above."
                : may("addSession")
                  ? "No sessions this week. Add a one-off session, or set a weekly slot up for the class."
                  : "No sessions this week."
            }
            {...(may("addSession") ? { action: <Button onClick={() => setShowCreate(true)}>Add a session</Button> } : {})}
          />
        ) : (
          <div className="flex flex-col gap-2.5">
            {daySessions.map((session) => {
              const register = progressOf.get(session.id);
              return (
                <OwnerSessionCard
                  key={session.id}
                  session={session}
                  timezone={timezone ?? ""}
                  attendance={register ? attendanceBadge(register.progress, register.session, timezone ?? "") : null}
                  onSelect={() => businessId && setSelected({ businessId, session })}
                />
              );
            })}
          </div>
        )}
      </div>

      {openSession && businessId ? (
        <SessionSheet
          key={openSession.id}
          session={openSession}
          businessId={businessId}
          timezone={timezone}
          onClose={() => setSelected(null)}
          onDone={(value) => {
            notice.show(value);
            setSelected(null);
          }}
        />
      ) : null}

      {showCreate && businessId ? (
        <CreateSessionSheet
          businessId={businessId}
          weekKeys={range.keys}
          todayKey={todayKey}
          timezone={timezone}
          onClose={() => setShowCreate(false)}
          onDone={(value) => {
            notice.show(value);
            setShowCreate(false);
          }}
        />
      ) : null}
    </OwnerGate>
  );
}

type ScheduleDay = {
  key: string;
  iso: string;
  weekday: string;
  dayOfMonth: string;
  count: number;
  isToday: boolean;
};

/** The picked day, today, or the first day of the window, in that order of preference. */
function resolveDay(days: ScheduleDay[], selectedKey: string | null): ScheduleDay | null {
  if (selectedKey) {
    const picked = days.find((day) => day.key === selectedKey);
    if (picked) return picked;
  }
  return days.find((day) => day.isToday) ?? days[0] ?? null;
}

function weekLabel(offset: number): string {
  if (offset === 0) return "This week";
  if (offset === 1) return "Next week";
  if (offset === -1) return "Last week";
  return offset > 0 ? `In ${offset} weeks` : `${Math.abs(offset)} weeks ago`;
}

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

/**
 * What a move or a cancel actually did, in the owner's own words.
 *
 * Both routes answer a `SessionMutationResult` carrying the fan-out counts, and those counts are
 * the single thing an owner wants confirmed after a change that reaches other people's phones. They
 * are read back rather than thrown away, and the zero case is called out explicitly: "nobody was
 * booked" is a materially different outcome from "everybody was told".
 */
function moveSummary(result: SessionMutationResult, timezone?: string): string {
  const when = formatDateTime(result.session.startsAt, timezone);
  if (result.affectedBookings === 0) {
    return `The session now starts ${when}. Nobody was booked, so there was nothing to tell anyone about.`;
  }
  return `${plural(result.affectedBookings, "booking", "bookings")} moved to ${when}, and ${plural(
    result.affectedUsers,
    "member was",
    "members were",
  )} notified.`;
}

function cancelSummary(result: SessionMutationResult): string {
  if (result.affectedBookings === 0) {
    return "The session is cancelled. Nobody was booked, so there was nothing to tell anyone about.";
  }
  const credits =
    result.creditsReturned > 0
      ? ` ${plural(result.creditsReturned, "credit went", "credits went")} back.`
      : "";
  return `${plural(result.affectedBookings, "booking", "bookings")} cancelled and ${plural(
    result.affectedUsers,
    "member was",
    "members were",
  )} notified.${credits}`;
}

/**
 * Move or cancel one session.
 *
 * Three modes in one sheet. The API has no dry-run for either action, so the blast radius shown
 * before the owner commits is computed here from the row's own `bookedCount`, and the real counts
 * are read back from the mutation result afterwards. That is the honest version: a promise made
 * from what is on screen, then a receipt from the server.
 */
function SessionSheet({
  session,
  businessId,
  timezone,
  onClose,
  onDone,
}: {
  session: AvailableSession;
  businessId: string;
  timezone?: string;
  onClose: () => void;
  onDone: (notice: OwnerNotice) => void;
}) {
  const [mode, setMode] = useState<"detail" | "move" | "cancel">("detail");
  // The facts change at the end (Came first): draw the panel again then (review F1.1).
  useSessionEnded(session.endsAt);
  // The front desk (T1) books and checks in here; the staff, a cover, a move or a cancel are the owner's.
  const { may } = useActiveBusiness();
  const [shift, setShift] = useState(0);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<unknown>(null);

  const navigate = useNavigate();
  const moveSession = useMoveSession(businessId);
  const cancelSession = useCancelSession(businessId);
  const openThread = useOpenMemberThread(businessId);
  // Only while the detail is on screen. Move and cancel replace this panel entirely, and refetching
  // a register nobody is looking at is a request for nothing.
  const attendees = useOwnerSessionAttendees(businessId, mode === "detail" ? session.id : null);

  // "Message" on a register row. The thread is opened (or found) server side, then the sheet gives
  // way to it: the owner is leaving this session for a conversation, so closing is the right exit.
  function message(memberUserId: string) {
    setError(null);
    openThread.mutate(memberUserId, {
      onSuccess: (row) => {
        onClose();
        void navigate({ to: "/owner/inbox/$conversationId", params: { conversationId: row.id } });
      },
      onError: setError,
    });
  }

  // The API answers CONFLICT for a move or a cancel on anything that is not scheduled, so a session
  // that has already been moved or cancelled shows its detail and nothing else.
  const isLive = session.status === "scheduled";
  const movedStart = new Date(new Date(session.startsAt).getTime() + shift * 60_000).toISOString();

  const shiftChips = useMemo<ChipOption[]>(
    () =>
      SHIFT_MINUTES.map((minutes) => ({
        id: String(minutes),
        label: minutes > 0 ? `+${minutes} min` : `${minutes} min`,
      })),
    [],
  );

  function back() {
    setMode("detail");
    setShift(0);
    setReason("");
    setError(null);
  }

  const title =
    mode === "move"
      ? "Move this session"
      : mode === "cancel"
        ? "Cancel this session"
        : session.groupName;

  return (
    <Sheet
      open
      onClose={onClose}
      title={title}
      subtitle={mode === "detail" ? formatDateTime(session.startsAt, timezone) : undefined}
      actions={
        mode === "detail" ? (
          isLive && (may("moveSession") || may("cancelSession")) ? (
            <>
              {may("moveSession") ? (
                <Button size="lg" fullWidth onClick={() => setMode("move")}>
                  Move session
                </Button>
              ) : null}
              {may("cancelSession") ? (
                <Button variant="secondary" fullWidth onClick={() => setMode("cancel")}>
                  Cancel session
                </Button>
              ) : null}
            </>
          ) : (
            <Button variant="secondary" size="lg" fullWidth onClick={onClose}>
              Close
            </Button>
          )
        ) : mode === "move" ? (
          <>
            <Button
              size="lg"
              fullWidth
              disabled={shift === 0}
              loading={moveSession.isPending}
              onClick={async () => {
                setError(null);
                try {
                  const result = await moveSession.mutateAsync({
                    sessionId: session.id,
                    startsAt: movedStart,
                  });
                  onDone({ title: "Session moved", message: moveSummary(result, timezone) });
                } catch (caught) {
                  setError(caught);
                }
              }}
            >
              {session.bookedCount === 0 ? "Move the session" : "Move and notify everyone"}
            </Button>
            <Button variant="secondary" fullWidth onClick={back}>
              Back
            </Button>
          </>
        ) : (
          <>
            <Button
              size="lg"
              fullWidth
              disabled={reason.trim().length < 3}
              loading={cancelSession.isPending}
              onClick={async () => {
                setError(null);
                try {
                  const result = await cancelSession.mutateAsync({
                    sessionId: session.id,
                    reason: reason.trim(),
                  });
                  onDone({ title: "Session cancelled", message: cancelSummary(result) });
                } catch (caught) {
                  setError(caught);
                }
              }}
            >
              {session.bookedCount === 0 ? "Cancel the session" : "Cancel and notify everyone"}
            </Button>
            <Button variant="secondary" fullWidth onClick={back}>
              Keep it
            </Button>
          </>
        )
      }
    >
      {error ? <InlineError error={error} /> : null}

      {mode === "detail" ? (
        <div>
          {!isLive ? (
            <Callout
              title={session.status === "moved" ? "This session was moved" : "This session was cancelled"}
            >
              {session.status === "moved"
                ? "Everyone booked went with it. The new time is elsewhere in this week, and this row stays as the record."
                : (session.cancelReason ??
                  "Members were notified and their credits were returned.")}
            </Callout>
          ) : null}
          {/* After the end: Came first, then Booked (`sessionFacts`, #ai-feedback 2026-10-26). */}
          {sessionFacts(session).map((fact) => (
            <FactRow key={fact.label} label={fact.label} value={fact.value} />
          ))}
          <FactRow label="Runs for" value={`${session.durationMin} min`} />
          <FactRow label="Source" value={session.templateId ? "Weekly slot" : "One off"} last={!isLive} />
          {/* The same controls as the desktop panel (`session-controls.tsx`), so the phone has them too. */}
          {isLive && may("setSessionStaff") ? (
            <SessionStaffChoice businessId={businessId} session={session} onError={setError} />
          ) : null}
          {isLive && may("askCover") ? <SessionCover businessId={businessId} sessionId={session.id} timezone={timezone} /> : null}

          {/*
            The register. Loaded with the sheet rather than behind a second tap, because "who is
            coming" is the question the session was opened for and a spinner the owner has to ask
            for is a spinner they read as a dead end. From the session's day on it opens "Take attendance".
          */}
          <SessionPeople
            businessId={businessId}
            session={session}
            attendees={attendees}
            timezone={timezone}
            onMessage={message}
            onError={setError}
            headClassName="pt-5 pb-2"
          />
        </div>
      ) : null}

      {mode === "move" ? (
        <div>
          <p className="pb-3 text-[14px] text-ink-2">
            Shift the start time. Everyone booked moves with it and gets a notification showing the
            old time and the new one.
          </p>
          <ChipRow
            options={shiftChips}
            selectedId={String(shift)}
            onSelect={(id) => setShift(Number(id))}
            label="How far to shift the start time"
            wrap
          />
          <div className="mt-4 rounded-card bg-accent-surface px-4 py-4 text-center">
            <p className="eyebrow">New time</p>
            <p className="text-[26px] font-semibold text-accent-strong">
              {formatTime(movedStart, timezone)}
            </p>
            <p className="mt-1 text-[13px] text-ink-2">
              was {formatTime(session.startsAt, timezone)}
            </p>
          </div>
          <p className="pt-3 pb-1 text-[13px] text-ink-2">
            {session.bookedCount === 0
              ? "Nobody is booked on this session, so nobody will be notified."
              : `${plural(session.bookedCount, "booking moves", "bookings move")} with it, and everyone on them is notified.`}
          </p>
        </div>
      ) : null}

      {mode === "cancel" ? (
        <div>
          <Callout
            tone="late"
            title={`${plural(session.bookedCount, "member", "members")} will be notified`}
          >
            Members are notified and their credits are returned. A business cancellation always returns
            the credit, so nobody loses a session because of a change on your side.
          </Callout>
          <div className="pb-2">
            <Field
              label="Reason, shown to members"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Venue maintenance"
              maxLength={200}
              hint="Keep it short. This goes out in the notification."
            />
          </div>
        </div>
      ) : null}
    </Sheet>
  );
}

/**
 * Create a one-off session outside the weekly slots.
 *
 * The day and the hour are chips rather than a native date and time picker, because the answer is
 * almost always "one of the seven days on screen, at one of the hours this business runs", and two taps
 * beat two spinner dialogs. The picked wall clock is interpreted in the business's timezone: 5pm means
 * 5pm at the pool, whichever country the owner happens to be reading this from.
 */
function CreateSessionSheet({
  businessId,
  weekKeys,
  todayKey,
  timezone,
  onClose,
  onDone,
}: {
  businessId: string;
  weekKeys: string[];
  todayKey: string;
  timezone?: string;
  onClose: () => void;
  onDone: (notice: OwnerNotice) => void;
}) {
  const groups = useOwnerGroups(businessId);
  const createSession = useCreateSession(businessId);

  const [groupId, setGroupId] = useState<string | null>(null);
  // Today when today is in the week on screen, the first day otherwise.
  const [dayIndex, setDayIndex] = useState(() => Math.max(0, weekKeys.indexOf(todayKey)));
  const [hour, setHour] = useState(17);
  const [capacity, setCapacity] = useState("");
  const [error, setError] = useState<unknown>(null);

  const options = groups.data ?? [];
  const chosen = options.find((row) => row.id === groupId) ?? options[0];

  const days = useMemo(
    () =>
      weekKeys.map((key) => ({
        id: key,
        label: formatWeekdayShort(instantFromZonedTime(key, 12, 0, timezone), timezone),
      })),
    [weekKeys, timezone],
  );

  const startsAt = useMemo(() => {
    const day = days[dayIndex] ?? days[0];
    if (!day) return new Date().toISOString();
    return instantFromZonedTime(day.id, hour, 0, timezone);
  }, [days, dayIndex, hour, timezone]);

  const hourChips = useMemo<ChipOption[]>(
    () => START_HOURS.map((value) => ({ id: String(value), label: hourLabel(value) })),
    [],
  );

  // Blank means "whatever this class normally takes", which is the right default and the reason the
  // field starts empty rather than pre-filled with a number the owner then has to read and approve.
  // The class's usual number can be null: no limit.
  const capacityValue = capacity.trim() === "" ? (chosen?.defaultCapacity ?? null) : Number(capacity);
  const canSubmit = Boolean(chosen) && (capacityValue === null || (Number.isInteger(capacityValue) && capacityValue > 0));

  return (
    <Sheet
      open
      onClose={onClose}
      title="Add a session"
      subtitle="A one off, outside your weekly slots"
      actions={
        <Button
          size="lg"
          fullWidth
          disabled={!canSubmit}
          loading={createSession.isPending}
          onClick={async () => {
            if (!chosen) return;
            setError(null);
            try {
              await createSession.mutateAsync({
                groupId: chosen.id,
                startsAt,
                capacity: capacityValue,
              });
              onDone({
                title: "Session added",
                message:
                  capacityValue === null
                    ? `${chosen.name} starts ${formatDateTime(startsAt, timezone)}, with no limit on places.`
                    : `${chosen.name} starts ${formatDateTime(startsAt, timezone)}, with room for ${capacityValue}.`,
              });
            } catch (caught) {
              setError(caught);
            }
          }}
        >
          Create session
        </Button>
      }
    >
      {error ? <InlineError error={error} /> : null}

      {groups.isLoading && !groups.data ? (
        <SkeletonList rows={2} />
      ) : options.length === 0 ? (
        <EmptyState
          title="No class types yet"
          message="A session belongs to a group. Create one for this business before adding sessions."
        />
      ) : (
        <>
          <p className="eyebrow pb-2 text-ink-2">Class</p>
          <div className="overflow-hidden rounded-card border border-line">
            {options.map((option, index) => (
              <OwnerListRow
                key={option.id}
                title={option.name}
                subtitle={`${option.durationMin} min · ${option.defaultCapacity === null ? "no limit on places" : `usually ${option.defaultCapacity} spots`}`}
                onSelect={() => setGroupId(option.id)}
                trailing={
                  chosen?.id === option.id ? (
                    <span className="rounded-pill bg-accent-surface px-2 py-0.5 text-[11px] font-semibold text-accent-strong">
                      Picked
                    </span>
                  ) : undefined
                }
                last={index === options.length - 1}
              />
            ))}
          </div>

          <p className="eyebrow pt-5 pb-2 text-ink-2">Day</p>
          <ChipRow
            options={days}
            selectedId={days[dayIndex]?.id ?? ""}
            onSelect={(id) => setDayIndex(Math.max(0, days.findIndex((day) => day.id === id)))}
            label="Which day the session runs"
            wrap
          />

          <p className="eyebrow pt-5 pb-2 text-ink-2">Start time</p>
          <ChipRow
            options={hourChips}
            selectedId={String(hour)}
            onSelect={(id) => setHour(Number(id))}
            label="What time the session starts"
            wrap
          />

          <div className="pt-5 pb-2">
            <Field
              label="Spots"
              value={capacity}
              onChange={(event) => setCapacity(event.target.value.replace(/[^0-9]/g, ""))}
              inputMode="numeric"
              maxLength={3}
              placeholder={typeof chosen?.defaultCapacity === "number" ? String(chosen.defaultCapacity) : "No limit"}
              hint={`Starts ${formatDateTime(startsAt, timezone)}. Leave it blank to use the usual number for this class.`}
            />
          </div>
        </>
      )}
    </Sheet>
  );
}

function hourLabel(hour: number): string {
  if (hour === 12) return "12pm";
  return hour > 12 ? `${hour - 12}pm` : `${hour}am`;
}
